'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import Image from 'next/image'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  CalendarIcon,
  CombineIcon,
  DownloadIcon,
  EditIcon,
  EyeIcon,
  FileBoxIcon,
  FolderGitIcon,
  GalleryThumbnailsIcon,
  HardDriveIcon,
  MoreHorizontalIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  TextIcon,
  Trash2Icon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  ContentFileIcon,
  DeleteConfirmationDialog,
  FilesFilters,
  LoadingState,
  SortableHeader
} from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { formatDateTime, formatFileSize, getResizedPictureUrl, textLimit } from '@/lib/utils'
import { useDeleteFile, useDownloadFile, useFiles, useBulkDeleteFiles } from '@/hooks'
import { AccessLevelEnum, FileStorageDriverEnum, SortEnum } from '@/enums'
import { BulkDeleteResponse } from '@/types/general'
import { FilesFilterParams } from '@/types/file'
import { useFilesStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteFileId, setDeleteFileId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  // State for tracking downloads is now managed in the store

  // File selection hooks from Zustand store
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'FILES', 'CREATE')
  const canShow = hasPermission(currentUser, 'FILES', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'FILES', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'FILES', 'DELETE')

  const selectedFileIds = useFilesStore(state => state.selectedFileIds)
  const toggleFileSelection = useFilesStore(state => state.toggleFileSelection)
  const selectAllFiles = useFilesStore(state => state.selectAllFiles)
  const deselectAllFiles = useFilesStore(state => state.deselectAllFiles)
  const isFileSelected = useFilesStore(state => state.isFileSelected)

  const { mutate: deleteFileMutate, isPending: isDeleting } = useDeleteFile()
  const { mutate: downloadFileMutate } = useDownloadFile()
  const { mutate: bulkDeleteFilesMutate, isPending: isBulkDeleting } = useBulkDeleteFiles()

  const filters = useMemo<FilesFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      drivers: params.get('drivers') ? (params.get('drivers')?.split(',') as FileStorageDriverEnum[]) : undefined,
      accessLevels: params.get('accessLevels')
        ? (params.get('accessLevels')?.split(',') as AccessLevelEnum[])
        : undefined,
      onlyImages: params.get('onlyImages') === 'true' ? true : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: filesData, isLoading, error, refetch, isFetching } = useFiles(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<FilesFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if ((key === 'drivers' || key === 'accessLevels') && Array.isArray(value)) {
          params.set(key, value.join(','))
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<FilesFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/files')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (id: string) => {
    router.push(`/files/${id}`)
  }

  const handleDownload = (id: string) => {
    downloadFileMutate(id, {
      onSuccess: () => {
        // Success is handled in the store
      },
      onError: error => {
        console.error('Error downloading file:', error)

        // Don't show error toast if it's a cancellation error
        const errorMessage = error?.message || String(error)
        const isCancellationError =
          errorMessage.toLowerCase().includes('canceled') ||
          errorMessage.toLowerCase().includes('canceled') ||
          errorMessage.toLowerCase().includes('abort')

        if (!isCancellationError) {
          toast.error(t('error_downloading_file'))
        }
      }
    })
  }

  const handleEdit = (id: string) => {
    router.push(`/files/${id}/edit`)
  }

  const handleDelete = (id: string) => {
    setDeleteFileId(id)
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem
    // We no longer need to check if the file is already in the download queue
    // since we now allow multiple downloads of the same file

    return (
      <>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleView(id)
            }}>
            <EyeIcon className="h-4 w-4" />
            {t('file_details')}
          </ItemComponent>
        )}

        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleDownload(id)
          }}>
          <DownloadIcon className="h-4 w-4" />
          {t('download')}
        </ItemComponent>
        {canUpdate && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleEdit(id)
            }}>
            <EditIcon className="h-4 w-4" />
            {t('edit_file')}
          </ItemComponent>
        )}
        {canDelete && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleDelete(id)
            }}
            className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  const confirmDeleteFile = () => {
    if (deleteFileId) {
      deleteFileMutate(deleteFileId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['files'] })
          toast.success(t('deleted_successfully'))
          setDeleteFileId(null)
        },
        onError: error => {
          console.error('Error deleting file:', error)
          toast.error(t('error_deleting'))
          setDeleteFileId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedFileIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteFiles = () => {
    if (selectedFileIds.length > 0) {
      bulkDeleteFilesMutate(selectedFileIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['files'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (filesData && filesData.items.length > 0) {
      if (selectedFileIds.length === filesData.items.length) {
        // If all files are selected, deselect all
        deselectAllFiles()
      } else {
        // Otherwise, select all files
        selectAllFiles(filesData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileBoxIcon className="h-5 w-5" />
                {t('files')}
              </CardTitle>
              <CardDescription>{t('files_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/files/create">
                    <SquarePlusIcon />
                    <span className="hidden items-center gap-2 lg:flex">{t('create_file')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedFileIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedFileIds.length})</span>
                  </div>
                </Button>
              )}
              <FilesFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {filesData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  filesData &&
                                  filesData.items.length > 0 &&
                                  selectedFileIds.length === filesData.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-37.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <GalleryThumbnailsIcon className="h-4 w-4" />
                            {t('preview')}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="name"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <TextIcon className="h-4 w-4" />
                            {t('name')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="path"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <FolderGitIcon className="h-4 w-4" />
                            {t('path')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30">
                          <SortableHeader
                            sortBy="type"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CombineIcon className="h-4 w-4" />
                            {t('type')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30 text-right">
                          <SortableHeader
                            sortBy="size"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <FileBoxIcon className="h-4 w-4" />
                            {t('size')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30">
                          <SortableHeader
                            sortBy="driver"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HardDriveIcon className="h-4 w-4" />
                            {t('driver')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30">
                          <SortableHeader
                            sortBy="accessLevel"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HardDriveIcon className="h-4 w-4" />
                            {t('access_level')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filesData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filesData.items.map((file, index) => (
                          <ContextMenu key={file.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(file.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={isFileSelected(file.id)}
                                        onCheckedChange={() => toggleFileSelection(file.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="flex min-h-20 items-center justify-center py-4">
                                  {file.type.startsWith('image/') ? (
                                    <div className="bg-checkerboard flex items-center justify-center rounded border p-0.5">
                                      <Image
                                        unoptimized
                                        src={getResizedPictureUrl(file.path, 120, 120)}
                                        alt={file.name}
                                        width={120}
                                        height={120}
                                        className={`mx-auto max-h-16 w-auto max-w-16 object-contain ${file.type === 'image/svg+xml' || file.path.toLowerCase().endsWith('.svg') ? 'max-w-22.5' : ''}`}
                                      />
                                    </div>
                                  ) : (
                                    <div className="flex justify-center">
                                      <div className="h-10 w-9 shrink-0">
                                        <ContentFileIcon file={file} />
                                      </div>
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>{textLimit(file.name, 120)}</span>
                                    <span className="text-muted-foreground text-xs">ID: {file.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell>{file.path}</TableCell>
                                <TableCell className="text-center">
                                  <div className="flex flex-row items-center justify-start gap-2">{file.type}</div>
                                </TableCell>
                                <TableCell className="text-right">{formatFileSize(file.size)}</TableCell>
                                <TableCell>{t(file.driver)}</TableCell>
                                <TableCell>{t(file.accessLevel)}</TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(file.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(file.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(file.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(file.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={filesData.page}
                  totalPages={filesData.pages}
                  totalItems={filesData.total}
                  pageSize={filesData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteFileId}
          onCancel={() => setDeleteFileId(null)}
          onConfirm={confirmDeleteFile}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteFiles}
          isDeleting={isBulkDeleting}
          description={t('bulk_delete_confirmation_message')}
        />
      )}
    </>
  )
}

export default List
